import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Database, Image, Users, Clock, RotateCw, Calendar, Eye, Heart } from 'lucide-react';
import { adminSyncCatalogue, adminSyncReport, adminPutSyncSettings, adminStatsReport, adminStatsSync } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-card border border-border rounded-2xl p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><Icon className="w-6 h-6 text-primary" /></div>
    <div><p className="text-2xl font-800 font-display">{value ?? '—'}</p><p className="text-sm text-muted-foreground font-700">{label}</p></div>
  </div>
);
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('fr-FR'); } catch (e) { return iso; } };
const StatusPill = ({ s }) => {
  const map = { success: ['Succès', 'text-green-600', CheckCircle2], partial: ['Partiel', 'text-amber-500', AlertTriangle], failed: ['Échec', 'text-destructive', AlertTriangle] };
  const [label, cls, Icon] = map[s] || ['—', 'text-muted-foreground', AlertTriangle];
  return <span className={`inline-flex items-center gap-1.5 font-800 ${cls}`}><Icon className="w-4 h-4" /> {label}</span>;
};

const SyncPanel = () => {
  const [report, setReport] = useState(null);
  const [hour, setHour] = useState('03:00');
  const [enabled, setEnabled] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsSyncing, setStatsSyncing] = useState(false);
  const { toast } = useToast();
  const poll = useRef(null);

  const loadStats = async () => { try { setStats(await adminStatsReport()); } catch (e) { /* noop */ } };
  const syncStats = async () => {
    if (!window.confirm('Lire les compteurs Vues/J’aime depuis Citytoon.org (lecture seule, aucune écriture WordPress) ?')) return;
    setStatsSyncing(true);
    try { const r = await adminStatsSync(); toast({ title: r.status === 'ok' ? `Statistiques synchronisées (${r.found})` : 'Aucun compteur exposé par l’API', description: r.message || '' }); await loadStats(); }
    catch (e) { toast({ title: 'Erreur', description: 'Synchronisation des statistiques impossible.' }); }
    finally { setStatsSyncing(false); }
  };

  const load = async () => {
    try {
      const r = await adminSyncReport();
      setReport(r);
      if (r.settings) { setEnabled(r.settings.enabled); setHour(r.settings.hour); }
      return r;
    } catch (e) { return null; }
  };
  useEffect(() => {
    load();
    loadStats();
    poll.current = setInterval(async () => { const r = await load(); if (r && !r.running && poll.current) { /* keep light polling */ } }, 4000);
    return () => clearInterval(poll.current);
  }, []);

  const sync = async () => {
    try {
      const res = await adminSyncCatalogue();
      if (res.running && !res.started) toast({ title: 'Déjà en cours', description: 'Une synchronisation est déjà en cours.' });
      else toast({ title: 'Synchronisation lancée', description: 'Suivi en temps réel ci-dessous.' });
      await load();
    } catch (e) { toast({ title: 'Erreur', description: 'Impossible de lancer la synchronisation.' }); }
  };

  const saveSettings = async (nextEnabled, nextHour) => {
    try { await adminPutSyncSettings(nextEnabled, nextHour); setEnabled(nextEnabled); setHour(nextHour); toast({ title: 'Réglages enregistrés' }); }
    catch (e) { toast({ title: 'Erreur', description: 'Enregistrement impossible.' }); }
  };

  const last = report?.last_report;
  const counts = report?.counts;
  const running = report?.running;

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-700 text-xl mb-1">Synchronisation du catalogue Toocheke</h2>
          <p className="text-sm text-muted-foreground font-600 max-w-xl">Source : citytoon.org (lecture seule, User-Agent <span className="font-mono">CitytoonEngageSync/1.0</span>). Upsert par <span className="font-mono">source_id</span> ; médias jamais copiés. En cas d'échec réseau, les données locales sont conservées.</p>
          <p className="text-sm font-700 mt-2 flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> Dernière synchro réussie : {fmt(report?.last_success_at)}</p>
          <p className="text-sm font-700 mt-1.5 flex items-center gap-1.5" data-testid="sync-api-version">
            <Database className="w-4 h-4 text-primary" /> Version API reçue : <span className="font-mono">{report?.last_api_version || 'inconnue'}</span>
            {report?.last_api_version && report.last_api_version !== '1.1.0' && (
              <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" /> portraits auteurs non automatisés (nécessite v1.1.0)</span>
            )}
            {report?.last_api_version === '1.1.0' && (
              <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" /> portraits auteurs disponibles</span>
            )}
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button onClick={sync} disabled={running} className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform disabled:opacity-60">
            {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />} {running ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </button>
          {last && last.status !== 'success' && !running && (
            <button onClick={sync} className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-muted text-foreground font-800 hover:bg-secondary/40 transition-colors"><RotateCw className="w-4 h-4" /> Réessayer la synchronisation</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={Database} label="Épisodes en base" value={counts?.episodes} />
        <Stat icon={Image} label="Images de lecture" value={counts?.images} />
        <Stat icon={Users} label="Auteurs et autrices" value={counts?.authors} />
      </div>

      {last && (
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-700 text-lg">Dernier rapport</h3><StatusPill s={last.status} /></div>
          <ul className="text-sm font-600 text-foreground/80 space-y-1">
            <li>Pages : <b>{last.pages}</b> · Créés : <b>{last.created}</b> · Mis à jour : <b>{last.updated}</b> · À vérifier : <b>{last.a_verifier}</b> · Erreurs : <b>{last.errors}</b></li>
            <li>Terminé : <b>{fmt(last.finished_at)}</b></li>
            {last.error && <li className="text-destructive font-700">Erreur : {last.error} {last.http_code ? `(HTTP ${last.http_code})` : ''}</li>}
          </ul>
          {last.attempts && last.attempts.length > 0 && (
            <details className="mt-3"><summary className="text-sm font-800 cursor-pointer text-primary">Détails des tentatives ({last.attempts.length})</summary>
              <div className="mt-2 space-y-1 text-xs font-mono text-muted-foreground">
                {last.attempts.map((a, i) => (<div key={i}>#{a.attempt} · {a.http || 'réseau'} · {a.url} · {fmt(a.at)} {a.error ? `· ${a.error}` : ''}</div>))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Daily sync (prepared, disabled by default) */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display font-700 text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" /> Synchronisation quotidienne</h3>
            <p className="text-sm text-muted-foreground font-600 max-w-md">À n'activer qu'après confirmation de la stabilité de l'API Citytoon sur plusieurs jours.</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => saveSettings(e.target.checked, hour)} className="w-5 h-5 accent-[#ed5c1f]" />
            <span className="font-800 text-sm">{enabled ? 'Activée' : 'Désactivée'}</span>
          </label>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm font-800">Heure :</span>
          <input type="time" value={hour} onChange={(e) => setHour(e.target.value)} onBlur={() => saveSettings(enabled, hour)} className="px-3 py-2 rounded-lg bg-muted font-700 outline-none" />
        </div>
      </div>

      {/* Statistiques Vues / J'aime (WordPress, lecture seule) */}
      <div className="bg-card border border-border rounded-2xl p-6" data-testid="stats-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="font-display font-700 text-lg flex items-center gap-2"><Eye className="w-5 h-5 text-primary" /> Statistiques Citytoon.org (Vues / J’aime)</h3>
            <p className="text-sm text-muted-foreground font-600 max-w-xl">Lecture seule : Citytoon Engage n’écrit jamais dans WordPress. Les compteurs s’affichent publiquement seulement s’ils existent.</p>
          </div>
          <button onClick={syncStats} disabled={statsSyncing} data-testid="stats-sync-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-800 text-sm disabled:opacity-60">{statsSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Synchroniser les statistiques</button>
        </div>
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm font-700">
            <div className="rounded-xl bg-muted p-3"><span className="flex items-center gap-1 text-muted-foreground"><Eye className="w-4 h-4" /> Avec vues</span><p className="text-xl font-800">{stats.with_views}</p></div>
            <div className="rounded-xl bg-muted p-3"><span className="flex items-center gap-1 text-muted-foreground"><Heart className="w-4 h-4" /> Avec j’aime</span><p className="text-xl font-800">{stats.with_likes}</p></div>
            <div className="rounded-xl bg-muted p-3"><span className="text-muted-foreground">Sans données</span><p className="text-xl font-800">{stats.sans_donnees}</p></div>
            <div className="rounded-xl bg-muted p-3"><span className="text-muted-foreground">Dernière synchro</span><p className="text-xs font-700">{fmt(stats.last_sync_at)} {stats.last_status ? `(${stats.last_status})` : ''}</p></div>
          </div>
        )}
        {stats && !stats.api_exposes_stats && (
          <p className="text-sm text-amber-600 font-700 mt-3 flex items-start gap-1.5" data-testid="stats-not-connected"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Statistiques Citytoon.org non connectées : intégration WordPress reportée. (Aucune valeur n’est affichée publiquement, jamais 0.)</p>
        )}
      </div>

      {/* History (last 10) */}
      {report?.history && report.history.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-6">
          <h3 className="font-display font-700 text-lg mb-3">Historique (10 dernières)</h3>
          <div className="space-y-1.5 text-sm">
            {report.history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-0">
                <StatusPill s={h.status} />
                <span className="text-muted-foreground font-600">{fmt(h.finished_at)}</span>
                <span className="font-700 text-xs">C:{h.created} · MàJ:{h.updated} · ÀV:{h.a_verifier} · Err:{h.errors}{h.http_code ? ` · HTTP ${h.http_code}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncPanel;
